import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { FamilyTagsComponent } from '../../../../shared/components/family-tags/family-tags.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  VendorActiveSubscriptionDisplay,
  buildVendorMembershipAlertMessage,
} from './vendor-active-subscription-display.util';
import { PaymentMethodCode } from '../../../../shared/types/payment-method.enum';
import {
  PhoneValidator,
  USA_PHONE_CUSTOM_INPUT_RULE,
} from '../../../../shared/validators/phone.validator';
import { CustomInputComponent, ValidationRule } from '../../../custom-input/custom-input.component';
import {
  VendorMembershipPaymentMethod,
  canConfirmVendorAddPayment,
  getDefaultVendorAddPaymentMethod,
  isVendorAddPaymentMethodEnabled,
  isVendorAddPaymentMethodVisible,
  shouldShowVendorAddPayment,
  toVendorUsaPhoneInputValue,
} from './vendor-active-add-payment.util';
import { PlanChangePanelComponent } from '../plan-change-panel/plan-change-panel.component';
import { PlanChangeResult } from '../../../../services/plan-change.service';
import { WashHistoryPanelComponent } from '../wash-history-panel/wash-history-panel.component';
import { OrderUtils } from '../../../../orders/utils/order.utils';
import { Subscription, filter, switchMap } from 'rxjs';
import {
  CustomerSubscription,
  CustomerSubscriptionService,
} from '../../../../services/customer-subscription.service';
import {
  SavedCard,
  SubscriptionPaymentMethodService,
} from '../../../../services/subscription-payment-method.service';
import { SubscriptionPauseActionsService } from '../../../../services/subscription-pause-actions.service';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../../../../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import { ToastService } from '../../../../services/toast.service';
import {
  cardWithExpiryLabel,
  renewalCardUpdatedToast,
} from '../../../../shared/utils/payment-method.copy';
import { ConfirmActionModalComponent } from '../../../../shared/components/confirm-action-modal/confirm-action-modal.component';
import { ManageSubscriptionMenuComponent } from '../../../../shared/components/manage-subscription-menu/manage-subscription-menu.component';
import { PauseSubscriptionModalComponent } from '../../../../shared/components/pause-subscription-modal/pause-subscription-modal.component';
import {
  PaymentMethodModalComponent,
  PaymentMethodSubject,
} from '../../../../shared/components/payment-method-modal/payment-method-modal.component';
import { SubscriptionPauseNoticeComponent } from '../../../../shared/components/subscription-pause-notice/subscription-pause-notice.component';
import { FamilyMembersModalComponent } from '../../../../shared/components/family-members-modal/family-members-modal.component';
import { StartFamilyModalComponent } from '../../../../shared/components/start-family-modal/start-family-modal.component';
import { SubscriptionResultModalComponent } from '../../../../shared/components/subscription-result-modal/subscription-result-modal.component';
import type {
  PauseOutcome,
  PauseSubject,
  SubscriptionPause,
} from '../../../../shared/types/subscription-pause.types';
import { PauseFlowController } from '../../../../shared/utils/pause-flow.controller';
import { FamilyMembersFlowController } from '../../../../shared/utils/family-members-flow.controller';
import { StartFamilyFlowController } from '../../../../shared/utils/start-family-flow.controller';
import { FamilyMembersService } from '../../../../services/family-members.service';
import { FamilyStartService } from '../../../../services/family-start.service';
import type { FamilyStartResult } from '../../../../shared/types/family-start.types';
import {
  importedBilledBy,
  isImportedMembershipSource,
} from '../../../../shared/utils/payment-method-access.util';
import {
  activateConfirmCopy,
  formatPauseDate,
  pauseRefusalCopy,
  subscriptionStatusLabel,
} from '../../../../shared/utils/subscription-pause.copy';
import {
  PauseViewState,
  cardStatus,
  pauseViewState,
  toDate,
} from '../../../../shared/utils/subscription-pause.util';
import {
  MembershipRewashEligibilityState,
  OrderRequestRewashTarget,
  OrderRequestTodayBannerState,
  PrepaidRewashEligibilityState,
  RewashEligibilityState,
} from '../../models/notification-order-request.models';

export type VendorActivePaymentMethod = VendorMembershipPaymentMethod;

export interface VendorActiveConfirmPaymentPayload {
  orderId: string | null;
  paymentMethod: VendorActivePaymentMethod;
  membershipStartDate: string;
  phoneNumber: string;
  cardholderName?: string;
  billingAddress?: string;
}

export interface VendorUpgradePlanOption {
  uniqueId: string;
  packageId: string;
  displayName: string;
  priceLabel: string;
  listPrice: number;
  themeColor: string | null;
}

export interface VendorUpgradeDiscountOption {
  id: string;
  label: string;
  priceLabel: string;
  mode: 'fixed' | 'custom';
}

/** CWS-1780: what a cancellation from this pop-up did, for the membership it ran on. */
interface VendorCancelOutcome {
  subscriptionId: string;
  status: 'cancelling' | 'cancelled';
  notice: { title: string; body: string };
}

export interface VendorUpgradePlanSelection {
  packageId: string;
  packageName: string;
  listPrice: number;
  discountId: string | null;
}

@Component({
  selector: 'app-vendor-active-subscription-modal',
  standalone: true,
  imports: [
    CommonModule,
    FamilyTagsComponent,
    FormsModule,
    CustomInputComponent,
    WashHistoryPanelComponent,
    PlanChangePanelComponent,
    CancelRefundPanelComponent,
    ConfirmActionModalComponent,
    ManageSubscriptionMenuComponent,
    PauseSubscriptionModalComponent,
    PaymentMethodModalComponent,
    FamilyMembersModalComponent,
    StartFamilyModalComponent,
    SubscriptionPauseNoticeComponent,
    SubscriptionResultModalComponent,
  ],
  templateUrl: './vendor-active-subscription-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vendor-active-subscription-modal.component.scss'],
})
export class VendorActiveSubscriptionModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Input() data: VendorActiveSubscriptionDisplay = {
    customerName: '—',
    licensePlate: '—',
    packageName: '—',
    isFamily: false,
    isPrimary: false,
    packageId: null,
    totalWashesAvailable: 'Unlimited',
    totalWashesRedeemed: '0',
    lastWashedDate: 'N/A',
    statusLabel: 'Subscription Active',
    sourceLabel: 'DRB',
    orderId: null,
    paymentStatus: null,
    paymentRequired: false,
    cardDetailsCollected: false,
    finalPrice: null,
    listPrice: null,
    membershipStartDate: '',
    membershipStartMinDate: '',
    renewalDateLabel: '',
    phoneNumber: '',
    subscriptionId: null,
    sourceRaw: null,
  };
  @Input() primaryButtonLabel = 'Got it';
  @Input() isPrimaryActionDisabled = false;
  /** Busy while confirm / Stripe / terminal is in flight (same as checkout). */
  @Input() isConfirmPaymentDisabled = false;
  /** Same device gate as payment-checkout Tap to Pay. */
  @Input() isMobileOrTablet = false;
  /** Same Terminal visibility as payment-checkout. */
  @Input() showTerminalPaymentMethod = false;
  /** Same Terminal enablement as payment-checkout. */
  @Input() isTerminalPaymentMethodEnabled = false;
  @Input() errorMessage = '';
  /** Parent Stripe Elements completion + cardholder (same rules as checkout). */
  @Input() isCardFormComplete = false;
  @Input() cardholderName = '';
  @Input() billingAddress = '';
  @Input() showUpgradePlanPicker = false;
  /** CWS-1426: Update Membership state (third body), fed by the parent. */
  @Input() showPlanChange = false;
  /** Whether the Update Membership CTA is offered (flag on at this location + Way membership). */
  @Input() canChangePlan = false;
  /** CWS-1469: Attendant+ / Admin may pause and reactivate; for Attendants those actions are hidden. */
  @Input() canManagePause = false;
  /** CWS-1780: Cancel membership is offered (cancel & refund on at this location, Way membership). */
  @Input() canCancelMembership = false;
  /** CWS-1581: Start Family is on here and the membership qualifies; the pop-up refuses the rest. */
  @Input() canStartFamily = false;
  /** The lane scan this pop-up was opened from; a scan of the primary's car skips the code. */
  @Input() lprEntryId: string | null = null;
  /** The lane's location; the plan-change and cancel panels both act for it. */
  @Input() planChangeLocationId: string | null = null;
  /** Success copy shown on the detail body after a change was applied or scheduled. */
  @Input() planChangeNotice: { title: string; body: string } | null = null;
  /** "Switches to X on <date> · $Y/mo" while a scheduled change is pending. */
  @Input() planChangeUpcoming: string | null = null;
  @Input() upgradePlans: VendorUpgradePlanOption[] = [];
  @Input() upgradeDiscounts: VendorUpgradeDiscountOption[] = [];
  @Input() isLoadingUpgradePlans = false;

  /** Shared wash-history panel (same contract as sales / create-order). */
  @Input() isLoadingHistory = false;
  @Input() historyBanner: OrderRequestTodayBannerState | null = null;
  @Input() historyExpanded = false;
  @Input() rewashEligibility: RewashEligibilityState | null = null;
  @Input() membershipRewashEligibility: MembershipRewashEligibilityState | null = null;
  /** CWS-1882: prepaid visit rewash candidates, passed straight to the history panel. */
  @Input() prepaidRewashEligibility: PrepaidRewashEligibilityState | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() upgradePlan = new EventEmitter<void>();
  @Output() planChange = new EventEmitter<void>();
  @Output() planChangeBack = new EventEmitter<void>();
  @Output() planChangeApplied = new EventEmitter<PlanChangeResult>();
  @Output() upgradePlanBack = new EventEmitter<void>();
  @Output() upgradePlanConfirm = new EventEmitter<VendorUpgradePlanSelection>();
  @Output() confirmPayment = new EventEmitter<VendorActiveConfirmPaymentPayload>();
  /** Notify parent so Stripe can mount when Card is selected. */
  @Output() paymentMethodChange = new EventEmitter<VendorActivePaymentMethod>();
  @Output() cardholderNameChange = new EventEmitter<string>();
  @Output() billingAddressChange = new EventEmitter<string>();
  @Output() historyToggle = new EventEmitter<void>();
  @Output() historyPageChange = new EventEmitter<number>();
  @Output() historyPageSizeChange = new EventEmitter<number>();
  @Output() rewashClick = new EventEmitter<OrderRequestRewashTarget>();
  /** A pause, activation or cancelled pause went through; the host refreshes what it lists. */
  @Output() pauseChanged = new EventEmitter<SubscriptionPause>();
  /** CWS-1780: a cancellation ran from this pop-up (whatever its refund outcome). */
  @Output() membershipCancelled = new EventEmitter<CancelRefundDone>();
  /** CWS-1581: a family was started from here; the lists behind the pop-up must follow. */
  @Output() familyStarted = new EventEmitter<FamilyStartResult>();

  readonly PaymentMethodCode = PaymentMethodCode;

  selectedPaymentMethod: VendorActivePaymentMethod = PaymentMethodCode.Card;
  membershipStartDate = '';
  phoneNumber = '';
  /** Same Show/Hide summary accordion pattern as payment-checkout. */
  showPaymentSummary = true;
  selectedUpgradePlanUniqueId: string | null = null;
  selectedUpgradeDiscountId: string | null = null;
  customDiscountExpanded = false;

  /**
   * CWS-1536: "Visa •••• 4242", or "—" with no renewal card; null leaves the field out. Set only
   * when svc-order says this membership's card can be changed by this user, so it also gates
   * Update Payment method.
   */
  cardOnFile: string | null = null;
  /** CWS-1536: an imported membership offers Payment method, as its vendor notice (no card). */
  importedPaymentMethod = false;
  /** CWS-1536: the membership the Payment method pop-up is open on. */
  paymentMethodSubject: PaymentMethodSubject | null = null;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly customerSubscriptions = inject(CustomerSubscriptionService);
  private readonly paymentMethods = inject(SubscriptionPaymentMethodService);
  private readonly toast = inject(ToastService);
  /** The membership "Card on file" was looked up for while the pop-up has been open. */
  private cardOnFileFor: string | null = null;
  private cardOnFileLookup: Subscription | null = null;
  /** CWS-1547: pause / Activate now for this membership. */
  readonly pauseFlow: PauseFlowController = inject(SubscriptionPauseActionsService).createFlow({
    resultHeader: outcome =>
      outcome.kind === 'paused' || outcome.kind === 'scheduled'
        ? 'Paused Subscription'
        : 'Active Subscription',
    onDone: outcome => this.onPauseDone(outcome),
    onStale: () => this.refreshPause(),
  });
  /**
   * The pause block after an action here. The host rebuilds `data` from its stale payload on every
   * check, so this outlives `data` rebuilds for the same membership; it is dropped as soon as
   * another membership is shown, and on close.
   */
  private pauseOverride: { subscriptionId: string; pause: SubscriptionPause } | null = null;

  /** CWS-1581: Start Family pop-up for this membership. */
  /** The add-members step that follows a started family. */
  readonly membersFlow = new FamilyMembersFlowController(inject(FamilyMembersService), {
    onChanged: () => this.cdr.markForCheck(),
  });
  readonly startFamilyFlow = new StartFamilyFlowController(inject(FamilyStartService), {
    members: this.membersFlow,
    onDone: (result, subject) => this.onFamilyStartDone(result, subject.subscriptionId),
  });
  /**
   * The membership a family was started on from here. The host rebuilds `data` from its stale
   * payload, so this keeps the Family / Primary tags until another membership is shown or the
   * pop-up closes.
   */
  private startedFamilyFor: string | null = null;

  /** CWS-1780: the cancel & refund panel is open over this pop-up, for this membership. */
  showCancelPanel = false;
  private cancelPanelSubscriptionId: string | null = null;
  /**
   * What a cancellation here did. The host rebuilds `data` from its stale payload, so this holds
   * the tag and notice until another membership is shown or the pop-up closes.
   */
  private cancelOutcome: VendorCancelOutcome | null = null;

  ngOnDestroy(): void {
    this.pauseFlow.destroy();
    this.startFamilyFlow.destroy();
    this.membersFlow.destroy();
    this.cardOnFileLookup?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['isVisible'] && !this.isVisible) ||
      (this.pauseOverride && this.pauseOverride.subscriptionId !== this.data?.subscriptionId)
    ) {
      // Another membership is shown: coming back to this one must read the host's latest state.
      this.pauseOverride = null;
    }
    if (
      (changes['isVisible'] && !this.isVisible) ||
      (this.cancelOutcome && this.cancelOutcome.subscriptionId !== this.data?.subscriptionId)
    ) {
      this.cancelOutcome = null;
    }
    if (
      (changes['isVisible'] && !this.isVisible) ||
      (this.startedFamilyFor && this.startedFamilyFor !== this.data?.subscriptionId)
    ) {
      this.startedFamilyFor = null;
    }
    // Another membership is shown (or the panel hid): the open flows belong to the old one.
    if (
      (changes['isVisible'] && !this.isVisible) ||
      (changes['data'] &&
        !changes['data'].firstChange &&
        changes['data'].previousValue?.subscriptionId !== this.data?.subscriptionId)
    ) {
      this.startFamilyFlow.close();
      this.membersFlow.close();
    }
    // The panel was opened for one membership. If the host now shows another (a new lane
    // read, the lane picker, a lookup result) the panel closes rather than retargeting
    // itself to a different customer's membership under the same title.
    if (
      this.showCancelPanel &&
      ((changes['isVisible'] && !this.isVisible) ||
        this.cancelPanelSubscriptionId !== (this.data?.subscriptionId ?? null))
    ) {
      this.closeCancelPanel();
    }
    if (changes['data'] && this.data) {
      // Keep default start date for prepare-payment; field is hidden in UI.
      this.membershipStartDate = this.data.membershipStartDate || '';
      this.phoneNumber = toVendorUsaPhoneInputValue(this.data.phoneNumber || '');
      this.clampMembershipStartDate();
      this.showPaymentSummary = true;
    }
    if (
      changes['data'] ||
      changes['isMobileOrTablet'] ||
      changes['showTerminalPaymentMethod'] ||
      changes['isTerminalPaymentMethodEnabled']
    ) {
      this.ensureValidSelectedPaymentMethod();
    }
    if (changes['isVisible'] && this.isVisible && this.showPaymentCollection) {
      this.paymentMethodChange.emit(this.selectedPaymentMethod);
    }
    if (changes['showUpgradePlanPicker'] || changes['upgradePlans'] || changes['data']) {
      this.seedUpgradePlanSelection();
    }
    this.syncCardOnFile();
  }

  /**
   * CWS-1536: "Card on file" for a Way membership, looked up once each time the pop-up opens on
   * it, not on every `data` rebuild, so a card changed elsewhere shows when it is reopened. An
   * imported membership has no card on file here; it offers Payment method (a card saved while its
   * vendor bills it) when its company switched that on and svc-order can save a card for it,
   * which needs a gate order at a shop with Stripe.
   */
  private syncCardOnFile(): void {
    // canManagePause is the Attendant+ / Admin check; svc-order refuses everyone else.
    const lookUp = this.isVisible && this.canManagePause;
    const subscriptionId = lookUp ? (this.data?.subscriptionId ?? null) : null;
    if (subscriptionId === this.cardOnFileFor) return;
    this.cardOnFileFor = subscriptionId;
    this.cardOnFileLookup?.unsubscribe();
    this.cardOnFile = null;
    this.importedPaymentMethod = false;
    this.paymentMethodSubject = null;
    if (!subscriptionId) return;
    // Same rule as plan change: a plate-scan payload carries no source at all, which is Way; and
    // svc-order refuses a Way-app membership's card itself.
    this.cardOnFileLookup = isImportedMembershipSource(this.data?.sourceRaw)
      ? this.lookUpImportedCards(subscriptionId)
      : this.lookUpCardOnFile(subscriptionId);
  }

  /** Only once Payment method is known to be on: switched off, an opening costs no request. */
  private lookUpCardOnFile(subscriptionId: string): Subscription {
    return this.paymentMethods
      .isEnabled()
      .pipe(
        filter(Boolean),
        switchMap(() => this.paymentMethods.renewalCard(subscriptionId))
      )
      .subscribe(card => {
        this.cardOnFile = card ? cardWithExpiryLabel(card) : '—';
        this.cdr.markForCheck();
      });
  }

  /** Only once the imported setting is known to be on: switched off, an opening costs no request. */
  private lookUpImportedCards(subscriptionId: string): Subscription {
    return this.paymentMethods
      .isImportedEnabled()
      .pipe(
        filter(Boolean),
        switchMap(() => this.paymentMethods.canSaveImportedCard(subscriptionId))
      )
      .subscribe(offered => {
        this.importedPaymentMethod = offered;
        this.cdr.markForCheck();
      });
  }

  /** Payment method can be offered: the card lookup answered, or a card can be saved for an import. */
  private get paymentMethodReady(): boolean {
    return this.cardOnFile !== null || this.importedPaymentMethod;
  }

  get title(): string {
    if (this.showPlanChange) return 'Update membership';
    if (this.showUpgradePlanPicker) return 'Select car wash plan';
    return this.pause?.state === 'paused' ? 'Paused Subscription' : 'Active subscription';
  }

  // ------------------------------------------------------------------ pause (CWS-1547)

  get pause(): SubscriptionPause | null {
    const override = this.pauseOverride;
    if (override && override.subscriptionId === this.data?.subscriptionId) {
      return override.pause;
    }
    return this.data?.pause ?? null;
  }

  /** How the card renders around a pause; null when pausing is off or the membership can't pause. */
  get pauseView(): PauseViewState | null {
    const pause = this.pause;
    if (!pause || !this.data?.subscriptionId) return null;
    // Only Way-billed memberships pause; the API says so with NOT_ELIGIBLE.
    if (pause.reason === 'NOT_ELIGIBLE' && pause.state === 'none') return null;
    return pauseViewState(
      { is_live: true, pause },
      {
        updateAvailable: this.canChangePlan,
        canManage: this.canManagePause,
        cancelAvailable: this.cancelOffered,
        paymentMethodAvailable: this.paymentMethodReady,
        startFamilyAvailable: this.startFamilyOffered,
      }
    );
  }

  /** Tag for a paused / pause-scheduled membership; null keeps the plain Active badge. */
  get pauseTag(): 'paused' | 'scheduled_pause' | null {
    const tag = cardStatus({ display_status: 'active', is_live: true, pause: this.pause });
    return tag === 'paused' || tag === 'scheduled_pause' ? tag : null;
  }

  get pauseTagLabel(): string {
    return subscriptionStatusLabel(this.pauseTag);
  }

  get pauseStatLabel(): string | null {
    const stat = this.pauseView?.stat;
    return stat ? (stat.key === 'resumes_on' ? 'Resumes on' : 'Pause starts') : null;
  }

  get pauseStatDate(): string {
    const stat = this.pauseView?.stat;
    return stat ? formatPauseDate(stat.date) : '';
  }

  private get actionsIdle(): boolean {
    return !(this.showPlanChange || this.pauseFlow.busy || this.cancelling);
  }

  /**
   * Manage Subscription: pause rules when the membership has a pause block, else plan change and
   * cancel and Payment method. Nothing is offered once the membership was cancelled from here.
   */
  get showManageMenu(): boolean {
    const view = this.pauseView;
    if (view) return this.actionsIdle && view.manage.visible;
    return (
      this.actionsIdle &&
      (this.canChangePlan ||
        this.cancelOffered ||
        this.showPaymentMethod ||
        this.startFamilyOffered)
    );
  }

  // ------------------------------------------------------------------ start family (CWS-1581)

  /** The host's answer, withdrawn once the membership is cancelling or already in a family. */
  private get startFamilyOffered(): boolean {
    return (
      this.canStartFamily &&
      !!this.data?.subscriptionId &&
      !this.data.isFamily &&
      this.startedFamilyFor !== this.data.subscriptionId &&
      !this.cancelling
    );
  }

  get showStartFamilyItem(): boolean {
    const view = this.pauseView;
    return view ? view.manage.startFamilyVisible : this.startFamilyOffered;
  }

  /** Family / Primary tags: the lane payload's, or the family just started here. */
  /** A family was started on the membership shown. */
  private get startedHere(): boolean {
    return !!this.startedFamilyFor && this.startedFamilyFor === this.data?.subscriptionId;
  }

  get isFamilyTag(): boolean {
    return this.data.isFamily || this.startedHere;
  }

  get isPrimaryTag(): boolean {
    return this.data.isPrimary || this.startedHere;
  }

  onManageStartFamily(): void {
    if (!this.startFamilyOffered || !this.data?.subscriptionId) return;
    this.startFamilyFlow.open({
      subscriptionId: this.data.subscriptionId,
      plate: this.data.licensePlate === '—' ? '' : this.data.licensePlate,
      packageName: this.data.packageName,
      lprEntryId: this.lprEntryId,
    });
  }

  private onFamilyStartDone(result: FamilyStartResult, subscriptionId: string): void {
    this.startedFamilyFor = subscriptionId;
    this.familyStarted.emit(result);
    this.cdr.markForCheck();
  }

  // ------------------------------------------------------------------ cancel (CWS-1780)

  /** The cancellation that ran here, for the membership shown. */
  private get cancelled(): VendorCancelOutcome | null {
    const outcome = this.cancelOutcome;
    return outcome && outcome.subscriptionId === this.data?.subscriptionId ? outcome : null;
  }

  /**
   * The membership is cancelling at its period end: cancelled from here, or read that way from
   * the lane payload. It still redeems until then; nothing in the menu applies any more.
   */
  private get cancelling(): boolean {
    return !!this.cancelled || !!this.data?.cancellation;
  }

  /** Cancel membership needs a membership to key on, and must not be offered twice. */
  private get cancelOffered(): boolean {
    return this.canCancelMembership && !!this.data?.subscriptionId && !this.cancelling;
  }

  get showCancelItem(): boolean {
    const view = this.pauseView;
    return view ? view.manage.cancelVisible : this.cancelOffered;
  }

  /**
   * "Cancelling" / "Cancelled" in place of the Active badge: after a cancel here, or
   * when the lane read a membership that is already cancelling.
   */
  get cancelledTagLabel(): string | null {
    const outcome = this.cancelled;
    if (outcome) return subscriptionStatusLabel(outcome.status);
    return this.data?.cancellation ? subscriptionStatusLabel('cancelling') : null;
  }

  get cancelNotice(): { title: string; body: string } | null {
    if (this.cancelled) return this.cancelled.notice;
    const cancellation = this.data?.cancellation;
    if (!cancellation) return null;
    const ends = toDate(cancellation.endsAt);
    return {
      title: 'Membership cancelled',
      body: ends
        ? `Allowed to wash until ${formatPauseDate(ends)}; the membership ends then.`
        : 'Allowed to wash until the end of the paid period; the membership ends then.',
    };
  }

  onManageCancel(): void {
    if (!this.cancelOffered) return;
    this.cancelPanelSubscriptionId = this.data?.subscriptionId ?? null;
    this.showCancelPanel = true;
  }

  closeCancelPanel(): void {
    this.showCancelPanel = false;
    this.cancelPanelSubscriptionId = null;
  }

  /**
   * The cancellation ran. The membership stays redeemable until its period end (or ended today),
   * so the pop-up stays open with the primary action; the tag and notice say what happened.
   */
  onCancelDone(done: CancelRefundDone): void {
    this.showCancelPanel = false;
    const subscriptionId = this.data?.subscriptionId;
    if (subscriptionId && done.result.status !== 'failed') {
      const membership = done.result.membership as { timing?: string } | null;
      this.cancelOutcome = {
        subscriptionId,
        status: membership?.timing === 'immediate' ? 'cancelled' : 'cancelling',
        notice: { title: 'Membership cancelled', body: done.summary },
      };
    }
    this.membershipCancelled.emit(done);
    this.cdr.markForCheck();
  }

  /**
   * CWS-1536: Update Payment method, for Attendant+ and Admin once the card lookup answered, or on
   * an imported membership whose company has it on.
   */
  get showPaymentMethod(): boolean {
    const view = this.pauseView;
    if (view) return view.manage.paymentMethodVisible;
    return this.canManagePause && this.paymentMethodReady;
  }

  /**
   * CWS-1536: open the shared Payment method pop-up. The lane payload has no renewal date or
   * price, so the membership is read first for the pop-up's summary line.
   */
  onManagePaymentMethod(): void {
    const subscriptionId = this.data?.subscriptionId;
    if (!subscriptionId || !this.showPaymentMethod) return;
    const open = (sub: CustomerSubscription | null) => {
      this.paymentMethodSubject = {
        subscriptionId,
        planName: sub?.package_name || this.data.packageName,
        plate: (sub?.license_plate || this.data.licensePlate || '').trim(),
        nextRenewal: sub?.next_billing_date ?? null,
        price: sub?.price ?? null,
        billedBy: importedBilledBy(this.data.sourceRaw),
      };
      this.cdr.markForCheck();
    };
    const customerId = this.data.customerId;
    if (!customerId) {
      open(null);
      return;
    }
    this.customerSubscriptions.getSubscription(customerId, subscriptionId).subscribe(sub => {
      // The popup closed or moved to another membership while it was loading.
      if (this.isVisible && this.data?.subscriptionId === subscriptionId) open(sub);
    });
  }

  /** The renewal card changed: show it at once; the next opening reads it back from Stripe. */
  onPaymentMethodUpdated(card: SavedCard): void {
    const toast = renewalCardUpdatedToast(card, this.paymentMethodSubject);
    this.paymentMethodSubject = null;
    this.cardOnFile = cardWithExpiryLabel(card);
    this.toast.showSuccess(toast.title, toast.detail);
    this.cdr.markForCheck();
  }

  closePaymentMethod(): void {
    this.paymentMethodSubject = null;
  }

  get showActivate(): boolean {
    return this.actionsIdle && this.pauseView?.showActivate === true;
  }

  /**
   * A paused membership cannot be redeemed or opened: Activate now is the only action. Attendants
   * don't get Activate now, so they keep the primary to dismiss.
   */
  get hidePrimary(): boolean {
    return this.pause?.state === 'paused' && this.canManagePause;
  }

  get flowError(): string {
    return pauseRefusalCopy(this.pauseFlow.errorCode);
  }

  get activateMessage(): string {
    return activateConfirmCopy(this.pauseFlow.activateTarget?.bankedDays);
  }

  onManagePause(): void {
    const base = this.pauseSubject();
    if (!base) return;
    const customerId = this.data.customerId;
    const open = (periodEnd: string | null) => {
      this.pauseFlow.openPause({ ...base, periodEnd });
      this.cdr.markForCheck();
    };
    // Without the period end the modal shows no projected renewal rather than a guess.
    if (!customerId) {
      open(null);
      return;
    }
    // The lane payload has no period end; read it so the modal can project "Next renewal date".
    this.customerSubscriptions.getSubscription(customerId, base.subscriptionId).subscribe(sub => {
      // The popup closed or moved to another membership while the date was loading.
      if (this.isVisible && this.data?.subscriptionId === base.subscriptionId) {
        open(sub?.current_period_end ?? null);
      }
    });
  }

  onManageCancelScheduledPause(): void {
    const subject = this.pauseSubject();
    if (subject) this.pauseFlow.askCancelScheduled(subject);
  }

  onActivateNow(): void {
    const subject = this.pauseSubject();
    if (subject) this.pauseFlow.askActivate({ ...subject, bankedDays: this.pause?.banked_days });
  }

  private pauseSubject(): PauseSubject | null {
    const subscriptionId = this.data?.subscriptionId;
    if (!subscriptionId) return null;
    return {
      subscriptionId,
      plate: this.data.licensePlate,
      planName: this.data.packageName,
      periodEnd: null,
      timeZone: this.pause?.time_zone ?? null,
    };
  }

  private onPauseDone(outcome: PauseOutcome): void {
    if (outcome.pause && this.data?.subscriptionId) {
      this.pauseOverride = { subscriptionId: this.data.subscriptionId, pause: outcome.pause };
      this.pauseChanged.emit(outcome.pause);
    } else {
      this.refreshPause();
    }
    this.cdr.markForCheck();
  }

  /** A stale pause (another device changed it): re-read the block and repaint. */
  private refreshPause(): void {
    const customerId = this.data?.customerId;
    const subscriptionId = this.data?.subscriptionId;
    if (!customerId || !subscriptionId) return;
    this.customerSubscriptions.getSubscription(customerId, subscriptionId).subscribe(sub => {
      // The popup closed or moved to another membership while the pause was loading.
      if (!sub?.pause || !this.isVisible || this.data?.subscriptionId !== subscriptionId) return;
      this.pauseOverride = { subscriptionId, pause: sub.pause };
      this.pauseChanged.emit(sub.pause);
      this.cdr.markForCheck();
    });
  }

  get showDetailBody(): boolean {
    return !this.showUpgradePlanPicker && !this.showPlanChange;
  }

  onManageUpdate(): void {
    this.planChange.emit();
  }

  onPlanChange(): void {
    this.planChange.emit();
  }

  onPlanChangeBack(): void {
    this.planChangeBack.emit();
  }

  onPlanChangeApplied(result: PlanChangeResult): void {
    this.planChangeApplied.emit(result);
  }

  get membershipStartMinDate(): string {
    return this.data?.membershipStartMinDate || this.data?.membershipStartDate || '';
  }

  get showPaymentCollection(): boolean {
    if (this.data?.cardDetailsCollected) {
      return false;
    }
    return shouldShowVendorAddPayment({
      paymentRequired: this.data?.paymentRequired,
      customerSource: this.data?.sourceLabel,
    });
  }

  get showMembershipStatusAlert(): boolean {
    return this.showPaymentCollection || Boolean(this.data?.cardDetailsCollected);
  }

  get isMembershipLimitReached(): boolean {
    return OrderUtils.isMembershipWashLimitExhausted(
      this.data?.totalWashesAvailable,
      this.data?.totalWashesRedeemed
    );
  }

  get membershipLimitReachedMessage(): string {
    return OrderUtils.ALL_REDEMPTIONS_USED_MESSAGE;
  }

  get alertMessage(): string {
    return buildVendorMembershipAlertMessage(
      this.data?.sourceLabel || 'DRB',
      this.data?.renewalDateLabel || '',
      Boolean(this.data?.cardDetailsCollected)
    );
  }

  get paymentMethodOptions(): VendorAddPaymentMethodOptionsView {
    return {
      isMobileOrTablet: this.isMobileOrTablet,
      showTerminalPaymentMethod: this.showTerminalPaymentMethod,
      isTerminalPaymentMethodEnabled: this.isTerminalPaymentMethodEnabled,
    };
  }

  get canConfirmPayment(): boolean {
    return canConfirmVendorAddPayment(
      this.selectedPaymentMethod,
      this.paymentMethodOptions,
      this.isConfirmPaymentDisabled,
      this.phoneNumber,
      this.isCardFormComplete
    );
  }

  get isPaymentLinkPhoneRequired(): boolean {
    return this.selectedPaymentMethod === PaymentMethodCode.Link;
  }

  get showInlineCardForm(): boolean {
    return this.showPaymentCollection && this.selectedPaymentMethod === PaymentMethodCode.Card;
  }

  get selectedPaymentMethodLabel(): string {
    switch (this.selectedPaymentMethod) {
      case PaymentMethodCode.TapToPay:
        return 'Tap to Pay';
      case PaymentMethodCode.Terminal:
        return 'Terminal';
      case PaymentMethodCode.Link:
        return 'Payment link';
      case PaymentMethodCode.Card:
      default:
        return 'Card';
    }
  }

  get formattedMembershipPrice(): string {
    const amount = Number(this.data?.listPrice ?? this.data?.finalPrice);
    if (!Number.isFinite(amount) || amount <= 0) {
      return '—';
    }
    return `$${amount.toFixed(2)}`;
  }

  /** Amount charged today (0 for imported membership card-on-file). */
  get formattedAmountDueToday(): string {
    const amount = Number(this.data?.finalPrice);
    if (!Number.isFinite(amount) || amount < 0) {
      return '$0.00';
    }
    return `$${amount.toFixed(2)}`;
  }

  get billingStartsLabel(): string {
    return this.data?.renewalDateLabel || this.data?.membershipStartDate || 'membership expiry';
  }

  /** Same US phone checks as payment-checkout; empty allowed except Payment Link. */
  get phoneValidationRules(): ValidationRule[] {
    const rules: ValidationRule[] = [];
    if (this.isPaymentLinkPhoneRequired) {
      rules.push({
        type: 'required',
        message: 'Phone number is required',
      });
    }
    rules.push({
      type: 'custom',
      value: (phone: string) => {
        const trimmed = String(phone ?? '').trim();
        if (!trimmed) {
          return true;
        }
        return PhoneValidator.isValidUsaPhone(trimmed);
      },
      message: USA_PHONE_CUSTOM_INPUT_RULE.message,
    });
    return rules;
  }

  isMethodVisible(method: VendorActivePaymentMethod): boolean {
    return isVendorAddPaymentMethodVisible(method, this.paymentMethodOptions);
  }

  isMethodEnabled(method: VendorActivePaymentMethod): boolean {
    return isVendorAddPaymentMethodEnabled(method, this.paymentMethodOptions);
  }

  onClose(): void {
    this.close.emit();
  }

  onPrimaryAction(): void {
    this.primaryAction.emit();
  }

  onUpgradePlan(): void {
    this.upgradePlan.emit();
  }

  onUpgradePlanBack(): void {
    this.upgradePlanBack.emit();
  }

  selectUpgradePlan(plan: VendorUpgradePlanOption): void {
    this.selectedUpgradePlanUniqueId = plan.uniqueId;
  }

  selectUpgradeDiscount(discount: VendorUpgradeDiscountOption): void {
    if (this.selectedUpgradeDiscountId === discount.id) {
      this.selectedUpgradeDiscountId = null;
      this.customDiscountExpanded = false;
      return;
    }
    this.selectedUpgradeDiscountId = discount.id;
    this.customDiscountExpanded = discount.mode === 'custom';
  }

  isUpgradePlanSelected(plan: VendorUpgradePlanOption): boolean {
    return this.selectedUpgradePlanUniqueId === plan.uniqueId;
  }

  isUpgradeDiscountSelected(discount: VendorUpgradeDiscountOption): boolean {
    return this.selectedUpgradeDiscountId === discount.id;
  }

  get canConfirmUpgradePlan(): boolean {
    return Boolean(this.selectedUpgradePlan);
  }

  onUpgradePlanConfirm(): void {
    const plan = this.selectedUpgradePlan;
    if (!plan) {
      return;
    }
    this.upgradePlanConfirm.emit({
      packageId: plan.packageId,
      packageName: plan.displayName,
      listPrice: plan.listPrice,
      discountId: this.selectedUpgradeDiscountId,
    });
  }

  togglePaymentSummary(): void {
    this.showPaymentSummary = !this.showPaymentSummary;
  }

  onConfirmPayment(): void {
    if (!this.showPaymentCollection || !this.canConfirmPayment) {
      return;
    }
    this.clampMembershipStartDate();
    this.confirmPayment.emit({
      orderId: this.data.orderId,
      paymentMethod: this.selectedPaymentMethod,
      membershipStartDate: this.membershipStartDate.trim(),
      phoneNumber: this.phoneNumber.trim(),
      cardholderName: this.cardholderName.trim(),
      billingAddress: this.billingAddress.trim(),
    });
  }

  selectPaymentMethod(method: VendorActivePaymentMethod): void {
    if (!this.isMethodEnabled(method)) {
      return;
    }
    this.selectedPaymentMethod = method;
    this.paymentMethodChange.emit(method);
  }

  onCardholderNameInput(value: string): void {
    this.cardholderNameChange.emit(value);
  }

  onBillingAddressInput(value: string): void {
    this.billingAddressChange.emit(value);
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  /** Membership start cannot be before expiry / auto-renewal date. */
  private clampMembershipStartDate(): void {
    const min = this.membershipStartMinDate;
    if (!min) {
      return;
    }
    if (!this.membershipStartDate || this.membershipStartDate < min) {
      this.membershipStartDate = min;
    }
  }

  private seedUpgradePlanSelection(): void {
    if (!this.showUpgradePlanPicker || this.upgradePlans.length === 0) {
      return;
    }
    if (
      this.selectedUpgradePlanUniqueId &&
      this.upgradePlans.some(plan => plan.uniqueId === this.selectedUpgradePlanUniqueId)
    ) {
      return;
    }
    const currentPackageId = String(this.data?.packageId || '').trim();
    const currentName = String(this.data?.packageName || '')
      .trim()
      .toLowerCase();
    const matched =
      this.upgradePlans.find(plan => plan.packageId === currentPackageId) ||
      this.upgradePlans.find(plan => plan.displayName.trim().toLowerCase() === currentName);
    this.selectedUpgradePlanUniqueId = matched?.uniqueId || this.upgradePlans[0].uniqueId;
  }

  private get selectedUpgradePlan(): VendorUpgradePlanOption | null {
    return (
      this.upgradePlans.find(plan => plan.uniqueId === this.selectedUpgradePlanUniqueId) || null
    );
  }

  private ensureValidSelectedPaymentMethod(): void {
    if (this.isMethodEnabled(this.selectedPaymentMethod)) {
      return;
    }
    this.selectedPaymentMethod = getDefaultVendorAddPaymentMethod(this.paymentMethodOptions);
  }
}

interface VendorAddPaymentMethodOptionsView {
  isMobileOrTablet: boolean;
  showTerminalPaymentMethod: boolean;
  isTerminalPaymentMethodEnabled: boolean;
}
